import { removeNullValues } from "@/helpers/object-helpers";
import { readLocalSetting, writeLocalSetting } from "@/services/userSettings";
import type { NavigationGuard, RouteLocationNormalized } from "vue-router";
import { defaultDraftListParameters, makeRouterQueryFromDraftListFiltersOnly } from "./draftListAdapters";
import type { DraftListOptions } from "./types";
import type { DraftListView } from "./components/DraftListToolbar.vue";

const STORAGE_KEY = "drafts";

const FILTER_QUERY_KEYS = ["mine", "notassigned", "cscAllRegion", "officeId", "searchTerm"] as const;

export type DraftListPreferences = {
  showOnlyOwn: boolean;
  showNotAssigned: boolean;
  cscAllRegion: boolean;
  officeId: string[];
  facet: Record<string, string[]>;
  sortField: number | null;
  sortDirection: number | null;
  /** Gruppera efter status. Not part of the URL. */
  grouped: boolean;
  /** Lista or Inkorg, so back from an ärende opened in the Inkorg returns to it. Not part of the URL. */
  view: DraftListView;
};

/** A link that already names its filters wins over what this browser remembers. */
export function hasDraftListQuery(query: RouteLocationNormalized["query"]): boolean {
  return (
    FILTER_QUERY_KEYS.some((key) => query[key] != null) || Object.keys(query).some((key) => key.startsWith("facet-"))
  );
}

export function draftListPreferencesFromOptions(
  options: DraftListOptions,
  grouped: boolean,
  view: DraftListView,
): DraftListPreferences {
  return normalizeDraftListPreferences({
    showOnlyOwn: options.showOnlyOwn,
    showNotAssigned: options.showNotAssigned,
    cscAllRegion: !!options.cscAllRegion,
    officeId: options.officeId,
    facet: options.facet ?? {},
    sortField: options.sortField ?? null,
    sortDirection: options.sortDirection ?? null,
    grouped,
    view,
  });
}

/** Stable key so a re-render of the same list does not write storage again. */
export function draftListPreferenceKey(options: DraftListOptions, grouped: boolean, view: DraftListView): string {
  return JSON.stringify(draftListPreferencesFromOptions(options, grouped, view));
}

export function queryFromDraftListPreferences(preferences: DraftListPreferences) {
  return removeNullValues({
    ...makeRouterQueryFromDraftListFiltersOnly({
      ...defaultDraftListParameters,
      showOnlyOwn: preferences.showOnlyOwn,
      showNotAssigned: preferences.showNotAssigned,
      cscAllRegion: preferences.cscAllRegion,
      officeId: preferences.officeId ?? [],
      facet: preferences.facet ?? {},
      searchTerm: null,
    }),
    sortField: preferences.sortField?.toString(),
    sortDirection: preferences.sortDirection?.toString(),
  });
}

function normalizeDraftListPreferences(preferences: DraftListPreferences): DraftListPreferences {
  const facet = Object.fromEntries(
    Object.entries(preferences.facet ?? {})
      .filter(([, values]) => values.length > 0)
      .map(([name, values]) => [name, [...values].sort()] as const)
      .sort(([a], [b]) => a.localeCompare(b)),
  );
  return {
    showOnlyOwn: preferences.showOnlyOwn,
    showNotAssigned: preferences.showNotAssigned,
    cscAllRegion: !!preferences.cscAllRegion,
    officeId: [...(preferences.officeId ?? [])].sort(),
    facet,
    sortField: typeof preferences.sortField === "number" ? preferences.sortField : null,
    sortDirection: typeof preferences.sortDirection === "number" ? preferences.sortDirection : null,
    grouped: !!preferences.grouped,
    // Anything else, and preferences saved before the Inkorg, open Lista.
    view: preferences.view === "inbox" ? "inbox" : "list",
  };
}

export function readDraftListPreferences(): DraftListPreferences | null {
  const parsed = readLocalSetting<Partial<DraftListPreferences>>(STORAGE_KEY);
  if (!parsed || typeof parsed.showOnlyOwn !== "boolean" || typeof parsed.showNotAssigned !== "boolean") {
    return null;
  }
  return normalizeDraftListPreferences({
    showOnlyOwn: parsed.showOnlyOwn,
    showNotAssigned: parsed.showNotAssigned,
    cscAllRegion: !!parsed.cscAllRegion,
    officeId: parsed.officeId ?? [],
    facet: parsed.facet ?? {},
    sortField: parsed.sortField ?? null,
    sortDirection: parsed.sortDirection ?? null,
    grouped: !!parsed.grouped,
    view: parsed.view ?? "list",
  });
}

export function saveDraftListPreferences(preferences: DraftListPreferences) {
  writeLocalSetting(STORAGE_KEY, normalizeDraftListPreferences(preferences));
}

/**
 * An empty /drafts-v2 (nav, back from an ärende) opens the list this browser last filtered.
 * A URL that already has filters is left alone, including links someone else sent.
 */
export const redirectToSavedDraftList: NavigationGuard = (to) => {
  if (hasDraftListQuery(to.query)) {
    return true;
  }
  const saved = readDraftListPreferences();
  if (!saved) {
    return true;
  }
  return { name: "drafts-v2", query: queryFromDraftListPreferences(saved), replace: true };
};
